/**
 * `studio/analytics.html`'s opening region — same strip as Tasks: answer, then
 * equal columns of caption + figure. MRR spends the page’s one `.hero-figure`;
 * the other three are `.sub-figure` (CONTRACT.md §2.5 / DESIGN.md P1).
 *
 * Three of the mockup's four tiles are drawn. Average revenue per client has no
 * field and is not invented; past due belongs to `/studio/money` and is linked,
 * not numbered, here (DESIGN.md P2).
 */

import { negate, type Period } from '@fareast/shared'
import { Link } from '@tanstack/react-router'

import { Region, RegionCols } from '../../../components/portal/Region.js'
import {
  Answer,
  HeroFigure,
  More,
  Muted,
  Small,
  SubFigure,
} from '../../../components/portal/Text.js'
import { HeroCapInfo } from '../../../components/studio/HeroCapInfo.js'
import { useFormat, type Formatter } from '../../../format/useFormat.js'
import type { AnalyticsReads, HealthData, MrrData, RateData, RevenueData } from './data.js'
import { UNDER_SCOPED_ID } from './ids.js'
import { Panel } from './Panel.js'
import { WHOLE_AMOUNT } from './render.js'
import { partitionScope } from './scope.js'

export type HeroRegionProps = {
  readonly reads: AnalyticsReads
  /** The guardrail month, so an in-page link does not silently reset it. */
  readonly period: Period
}

export function HeroRegion({ reads, period }: HeroRegionProps) {
  const format = useFormat()
  const retainers = reads.packageMix.data?.total.clients ?? null

  return (
    <div className="studio-hero studio-hero--n4">
      <Region>
        <Panel query={reads.mrr} subject="recurring revenue">
          {(mrr) => <Answer>{mrrAnswer(mrr, format)}</Answer>}
        </Panel>

        <RegionCols>
          <Panel query={reads.mrr} subject="recurring revenue">
            {(mrr) => <MrrColumn mrr={mrr} retainers={retainers} />}
          </Panel>
          <Panel query={reads.revenue} subject="revenue by billing model">
            {(revenue) => <RevenueColumn revenue={revenue} />}
          </Panel>
          <Panel query={reads.rate} subject="the effective rate">
            {(rate) => <RateColumn rate={rate} />}
          </Panel>
          <Panel query={reads.health} subject="client health">
            {(health) => <ScopeColumn health={health} period={period} />}
          </Panel>
        </RegionCols>

        <Small>
          <Muted>
            What is owed, what failed and what the billing run did are on{' '}
            <More asChild>
              <Link to="/studio/money">the money page →</Link>
            </More>
          </Muted>
        </Small>
      </Region>
    </div>
  )
}

/* ------------------------------------------------------------------ columns */

function MrrColumn({
  mrr,
  retainers,
}: {
  readonly mrr: MrrData
  readonly retainers: number | null
}) {
  const format = useFormat()
  const latest = mrr.series.find((point) => point.month === mrr.as_of) ?? mrr.series.at(-1)
  const label = retainers === null ? 'MRR' : `MRR · ${format.number(retainers)} live`

  return (
    <div>
      <HeroCapInfo
        label={label}
        tip="Monthly recurring revenue — sum of every live retainer as of the month in the answer. Live means the retainer is active at that date."
      />
      <HeroFigure value={latest === undefined ? '—' : format.money(latest.mrr, WHOLE_AMOUNT)} />
    </div>
  )
}

function RevenueColumn({ revenue }: { readonly revenue: RevenueData }) {
  const format = useFormat()
  const month = revenue.months.at(-1)?.month
  const label = month === undefined ? 'Revenue' : `${format.month(month)} revenue`

  return (
    <div>
      <HeroCapInfo
        label={label}
        tip="Recognised revenue for the month — retainer, project, extra and usage together."
      />
      <SubFigure value={format.money(revenue.current.total, WHOLE_AMOUNT)} />
    </div>
  )
}

function RateColumn({ rate }: { readonly rate: RateData }) {
  const format = useFormat()
  const under = rate.clients.filter((one) => one.under_floor).length
  const tip =
    rate.portfolio === null
      ? 'Nobody logged retainer time this month, so there is no effective rate.'
      : under === 0
        ? 'Retainer revenue over retainer time, across the whole book.'
        : `Retainer revenue over retainer time. ${format.number(under)} ${under === 1 ? 'client' : 'clients'} under the ${format.money(rate.floor, WHOLE_AMOUNT)} floor.`

  return (
    <div>
      <HeroCapInfo label="Effective $/hour" tip={tip} />
      <SubFigure
        value={rate.portfolio === null ? '—' : format.money(rate.portfolio, WHOLE_AMOUNT)}
      />
      {under === 0 ? null : (
        <Small>
          <Muted>
            {`${format.number(under)} under ${format.money(rate.floor, WHOLE_AMOUNT)} floor`}
          </Muted>
        </Small>
      )}
    </div>
  )
}

function ScopeColumn({ health, period }: { readonly health: HealthData; readonly period: Period }) {
  const format = useFormat()
  const { over, watch } = partitionScope(health)

  return (
    <div>
      <HeroCapInfo
        label="Under-scoped"
        tip="Packages delivering more value than they bill, two months running. Press the number to jump to the meters."
      />
      <Link
        to="/studio"
        search={{ period }}
        hash={UNDER_SCOPED_ID}
        aria-label="Packages under-scoped"
      >
        <SubFigure value={format.number(over.length)} />
      </Link>
      {watch.length === 0 ? null : (
        <Small>
          <Muted>{`${format.number(watch.length)} more above target`}</Muted>
        </Small>
      )}
    </div>
  )
}

/* ------------------------------------------------------------------- answer */

/**
 * The panel's one sentence, about the **movement** rather than the level.
 *
 * **"Nothing started, nothing ended" is checked against all four components, not
 * against `net`.** `net = new + expansion + contraction + churn`, so a month where a
 * retainer starts and another of the same size cancels nets to zero — and the page
 * would have stated, as a fact, that neither happened.
 */
function mrrAnswer(mrr: MrrData, format: Formatter): string {
  const month = format.monthLong(mrr.as_of)
  const movement = mrr.movements.find((one) => one.month === mrr.as_of)

  if (movement === undefined) return `Recurring revenue as it stood at the end of ${month}.`

  const still =
    movement.new.amount === 0 &&
    movement.expansion.amount === 0 &&
    movement.contraction.amount === 0 &&
    movement.churn.amount === 0
  if (still) {
    return `Recurring revenue did not move in ${month} — nothing started, nothing ended and no package changed.`
  }

  if (movement.net.amount === 0) {
    return `Recurring revenue ended ${month} where it began — what was added and what was lost cancelled out.`
  }

  const grew = movement.net.amount > 0
  const size = format.money(grew ? movement.net : negate(movement.net), WHOLE_AMOUNT)
  return `Recurring revenue ${grew ? 'grew by' : 'fell by'} ${size} in ${month}.`
}
